/**
 * Parse a Google Places address string into individual components.
 *
 * Google Places returns addresses in various formats depending on the country:
 *   - NL: "Keizersgracht 123, 1015 CJ Amsterdam, Netherlands"
 *   - ES: "C. de Narváez, 68, Retiro, 28009 Madrid, Spain"
 *   - DE: "Friedrichstraße 43, 10117 Berlin, Germany"
 *   - FR: "12 Rue de Rivoli, 75004 Paris, France"
 *   - UK: "221B Baker St, London NW1 6XE, UK"
 *   - BE: "Rue de la Loi 16, 1000 Brussels, Belgium"
 *   - IT: "Via Roma 1, 00184 Roma RM, Italy"
 *
 * The key insight: the postal code + city are always in ONE comma-separated part,
 * and it's typically the second-to-last part (last being the country).
 * Everything before that is part of the street address.
 */

export interface ParsedAddress {
  street: string
  postcode: string
  city: string
}

// Postal code patterns for European countries (ordered from most specific to least)
const POSTAL_CODE_PATTERNS = [
  // Dutch: 4 digits + space + 2 uppercase letters (e.g. "1015 CJ")
  /^(\d{4}\s?[A-Z]{2})\s+(.+)$/i,
  // UK: complex format (e.g. "NW1 6XE", "EC1A 1BB", "W1A 0AX")
  /^([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\s*(.*)$/i,
  // Most EU countries: 5 digits (ES, DE, FR, IT) — e.g. "28009 Madrid", "10117 Berlin"
  /^(\d{5})\s+(.+)$/,
  // Belgian/Swiss: 4 digits (e.g. "1000 Brussels", "8001 Zürich")
  /^(\d{4})\s+(.+)$/,
]

/**
 * Parse a Google Places formatted address into street, postcode, and city.
 */
export function parseGoogleAddress(address: string): ParsedAddress {
  if (!address) {
    return { street: '', postcode: '', city: '' }
  }

  const parts = address.split(',').map((p) => p.trim())

  // Scan all parts to find the one that contains a postal code
  for (let i = 0; i < parts.length; i++) {
    for (const pattern of POSTAL_CODE_PATTERNS) {
      const match = parts[i].match(pattern)
      if (match) {
        // Everything before this part is the street address
        const street = parts.slice(0, i).join(', ')
        const postcode = match[1].trim()
        // City is the rest of this part after the postcode.
        // If empty (UK sometimes puts city in a separate part), take the next part.
        let city = match[2]?.trim() || ''
        if (!city && i + 1 < parts.length) {
          city = parts[i + 1].trim()
        }
        // Clean up: remove country suffixes from city if present (e.g. "Roma RM" → "Roma RM")
        return { street, postcode, city }
      }
    }
  }

  // Fallback: no postal code found — use first part as street, rest as city
  return {
    street: parts[0] || '',
    postcode: '',
    city: parts.slice(1, -1).join(', ') || parts[1] || '', // exclude last part (likely country)
  }
}
